import { ReactNode, useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react'
import { useGSAP } from '@gsap/react'
import clsx from 'clsx'
import gsap from 'gsap'
// import { Draggable } from 'gsap/dist/Draggable'
// import ScrollTrigger from 'gsap/dist/ScrollTrigger'
// import ScrollToPlugin from 'gsap/dist/ScrollToPlugin'
// import useElementsScroll from '@hooks/tools/use-elements-scroll'

// gsap.registerPlugin(Draggable)
// gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

type SlidingTabsRef = {
  updatePosition: (newIndex: number, immediate?: boolean, callBack?: Function) => void
}

type BaseProps = {
  initIndex?: number
  fromVars?: gsap.TweenVars
  toVars?: gsap.TweenVars
  prohibitTabClick?: boolean
  sectionTitle?: string
  tabLabels: Array<string>
  tabsBoxClassName?: string
  containerClassName?: string
  tabsContainerClassName?: string
  contentBoxClassName?: string
  sectionTitleClassName?: string
  childrenBoxClassName?: string
  bgRefClassName?: string
  tabEleClassName?: string
  bgColor?: string
  activeTabChange?: (activeIndex: number) => void
  tabClickPrevCallback?: (clickIndex: number) => void
  gsapOnCompleteCallback?: (clickIndex: number | string) => void
}

type SlidingTabsProps = {
  type?: 'col' | 'row' | 'dynamic'
  opacitySwitch?: boolean
  children?: ReactNode[]
} & BaseProps

const SlidingTabs = forwardRef<SlidingTabsRef, SlidingTabsProps>(
  (
    {
      type = 'row',
      opacitySwitch = true,
      initIndex,
      fromVars,
      toVars,
      prohibitTabClick = false,
      sectionTitle,
      tabLabels = [],
      containerClassName,
      tabsBoxClassName,
      tabsContainerClassName,
      contentBoxClassName,
      sectionTitleClassName,
      childrenBoxClassName,
      bgColor,
      activeTabChange,
      children,
      tabClickPrevCallback,
      gsapOnCompleteCallback,
      bgRefClassName,
      tabEleClassName,
    },
    ref
  ) => {
    const [init, setInit] = useState<boolean>(true)
    const [activeIndex, setActiveIndex] = useState<number>(0)
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
    const bgRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const prevIndexRef = useRef<number>(0)
    const initPositionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const rowContentBoxRef = useRef<HTMLDivElement>(null)
    // const { elementsRef, activeId } = useElementsScroll({ open: !init })

    const elementsRef = useRef<Map<number, HTMLElement>>(new Map())
    const [activeId, setActiveId] = useState<number>(0)
    const isPauseObserve = useRef<boolean>(false)

    const cancelInitPositionUpdate = () => {
      if (initPositionTimerRef.current === null) return

      clearTimeout(initPositionTimerRef.current)
      initPositionTimerRef.current = null
    }

    useImperativeHandle(ref, () => ({
      updatePosition: (newIndex: number, immediate: boolean = false, callBack?: Function) => {
        // 外部状态同步后，旧的初始化任务不能再覆盖当前选中项。
        cancelInitPositionUpdate()
        if (type === 'col') {
          setActiveId(newIndex)
        } else {
          setActiveIndex(newIndex)
          scrollToCenter(newIndex)
          updateBgPosition(newIndex, immediate, callBack)
        }
      },
    }))

    // 更新背景动画
    const updateBgPosition = (newIndex: number, immediate: boolean = false, callBack?: Function) => {
      const currentTab = tabRefs.current[newIndex]
      const prevTab = tabRefs.current[prevIndexRef.current]

      if (!currentTab || !prevTab || !bgRef.current) {
        callBack && callBack(0)

        return
      }

      const { offsetLeft: newLeft, offsetWidth: newWidth } = currentTab
      const { offsetLeft: oldLeft, offsetWidth: oldWidth } = prevTab

      if (immediate) {
        gsap.set(bgRef.current, {
          left: newLeft,
          width: newWidth,
        })

        prevIndexRef.current = newIndex
        callBack && callBack(1)

        return
      }

      // 创建滑动动画
      gsap.fromTo(
        bgRef.current,
        {
          left: oldLeft,
          width: oldWidth,
          ...fromVars,
        },
        {
          left: newLeft,
          width: newWidth,
          duration: 0.2,
          delay: 0,
          ease: 'ease-in-out',
          overwrite: true,
          immediateRender: true,
          onComplete: () => {
            setTimeout(() => {
              isPauseObserve.current = false

              callBack && callBack(2)
            }, 500)
          },
          ...toVars,
        }
      )

      prevIndexRef.current = newIndex
    }

    //判断tab是否显示
    const isTabVisible = useMemo(() => {
      return tabLabels.filter((tab) => tab).length > 0
    }, [tabLabels])

    // 处理tab点击
    const handleTabClick = (index: number, id?: number | string) => {
      tabClickPrevCallback && tabClickPrevCallback(index)
      if (prohibitTabClick) return

      cancelInitPositionUpdate()
      setActiveIndex(index)
      scrollToCenter(index)
      updateBgPosition(index, false, gsapOnCompleteCallback && gsapOnCompleteCallback)

      if (type === 'col' && !!elementsRef.current?.get(index)) {
        // gsap.to('#main', { duration: 1, scrollTo: { y: `#col${index}`, offsetY: 60 } })

        isPauseObserve.current = true

        elementsRef.current.get(index)?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      }

      if (!!activeTabChange) {
        activeTabChange(index)
        isPauseObserve.current = true
      }
    }

    // 滚动到居中位置逻辑
    const scrollToCenter = (index: number) => {
      const tab = tabRefs.current[index]
      const container = containerRef.current
      if (!tab || !container) return

      // 计算目标 Tab 的居中位置
      const containerWidth = container.offsetWidth
      const tabWidth = tab.offsetWidth
      const tabOffsetLeft = tab.offsetLeft
      const targetScrollLeft = tabOffsetLeft - containerWidth / 2 + tabWidth / 2

      // 使用 GSAP 实现平滑滚动
      gsap.to(container, {
        scrollLeft: targetScrollLeft,
        duration: 0.3,
        ease: 'ease-in-out',
        overwrite: true,
      })
    }

    // tab组件内监听滚轮事件，阻止原生滚轮滚动，并实现横向滚动
    useGSAP(
      () => {
        const container = containerRef.current
        const content = contentRef.current
        if (!container || !content) return

        const handleWheel = (e: WheelEvent) => {
          e.preventDefault()

          const { scrollWidth, clientWidth, scrollLeft } = container

          const maxScroll = scrollWidth - clientWidth
          const delta = e.deltaY * 1.6 // 调整滚动速度
          const targetScroll = scrollLeft + delta

          // 平滑滚动
          gsap.to(container, {
            scrollLeft: Math.max(0, Math.min(targetScroll, maxScroll)),
            duration: 0.2,
            ease: 'ease-in-out',
          })
        }

        const { scrollWidth, clientWidth } = container

        // 未出现滚动轴，不挂载滚动事件
        if (scrollWidth > clientWidth) {
          container.addEventListener('wheel', handleWheel)
        }

        // 计算可滚动范围
        const getBounds = () => {
          const containerWidth = container.offsetWidth
          const contentWidth = content?.scrollWidth || 0
          const maxTranslate = containerWidth - contentWidth

          return { minX: Math.min(maxTranslate, 0), maxX: 0 }
        }

        // // 初始化拖拽实例
        // const draggable = new Draggable(content, {
        //   type: 'x',
        //   inertia: true,
        //   bounds: getBounds(),
        //   edgeResistance: 0.8,
        //   onDragStart: () => {
        //     gsap.set(content, { cursor: 'grabbing' })
        //   },
        //   onDragEnd: () => {
        //     gsap.set(content, { cursor: 'grab' })
        //   },
        // })

        // // 设置初始光标样式
        // gsap.set(content, { cursor: 'grab' })

        // 清理函数
        return () => {
          // draggable.kill()
          container.removeEventListener('wheel', handleWheel)
        }
      },
      { scope: containerRef, dependencies: [init] }
    )

    useEffect(() => {
      if (!(type === 'col') || isPauseObserve.current) return

      setActiveIndex(activeId)
      scrollToCenter(activeId)
      updateBgPosition(activeId, false)
    }, [activeId, type])

    // 处理初始化时，ref未挂载bug
    // 初始化背景位置
    useEffect(() => {
      if (!tabLabels.length || !init) return

      setActiveIndex(initIndex || 0)
      setInit(false)
      // 处理初始化时未获取准确currentTab的offsetWidth值
      cancelInitPositionUpdate()
      initPositionTimerRef.current = setTimeout(() => {
        updateBgPosition(initIndex || 0, true)
        initPositionTimerRef.current = null
      }, 150)
    }, [init, tabLabels])

    useEffect(() => {
      return () => cancelInitPositionUpdate()
    }, [])

    const handleObserve = (index: number, el: HTMLElement) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 获取元素在视口中的可见比例 (0~1)
            const ratio = entry.intersectionRatio

            if (ratio > 0.5) {
              setActiveId(index)
            }
          })
        },
        {
          threshold: [0.5],
        }
      )

      // 开始观察元素
      observer.observe(el)
    }

    return (
      <div className={clsx(containerClassName)}>
        <div
          className={clsx(
            'w-full',
            'laptop:flex laptop:items-center',
            {
              'sticky top-0 z-10': type === 'col',
              'justify-center': !sectionTitle,
              'justify-between': !!sectionTitle,
            },
            tabsBoxClassName
          )}
        >
          {!!sectionTitle && (
            <div
              className={clsx(
                sectionTitleClassName,
                'text-fc-primary font-semibold text-24 mb-29px mr-24px',
                'laptop:text-32 laptop:mb-0'
              )}
            >
              {sectionTitle}
            </div>
          )}

          {/* Tab 按钮容器 */}
          <div
            ref={containerRef}
            className={clsx(
              tabsContainerClassName,
              isTabVisible ? '' : 'hidden',
              'px-6',
              'overflow-x-scroll scroll-hidden',
              // 'laptop:flex-shrink-0 laptop:flex-grow-0',
              'laptop:px-0'
            )}
          >
            <div
              ref={contentRef}
              className={clsx(
                'relative flex-none flex z-10 rounded-full',
                'gap-1 w-fit p-4px',
                bgColor ? bgColor : 'bg-container-third'
              )}
            >
              {/* 滑动背景 */}
              <div
                ref={bgRef}
                className={clsx(
                  'absolute top-1/2 transition-all translate-y-[-50%] duration-100 ease-in-out',
                  'bg-brand-primary rounded-full',
                  'h-38px',
                  'laptop:h-48px',
                  bgRefClassName
                )}
              />

              {tabLabels.map((tab, index) => {
                return (
                  <button
                    key={index}
                    ref={(el) => {
                      return (tabRefs.current[index] = el)
                    }}
                    className={clsx(
                      'relative transition-colors',
                      'text-center font-normal whitespace-nowrap',
                      'px-4 py-2 text-14',
                      'laptop:px-6 laptop:py-3 laptop:text-base',
                      activeIndex === index ? 'text-anti-wh' : 'text-fc-secondary hover:text-fc-primary',
                      tabEleClassName
                    )}
                    onClick={() => handleTabClick(index)}
                  >
                    {tab}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* 内容区域：横向切换 */}
        {type === 'row' && !!children && (
          <div ref={rowContentBoxRef} className={clsx(contentBoxClassName, 'relative')}>
            {children.map((content, index) => (
              <div
                key={index}
                className={clsx(
                  childrenBoxClassName,
                  opacitySwitch && 'absolute !h-full',
                  opacitySwitch && 'transition-opacity duration-300',
                  'w-full h-fit',
                  !opacitySwitch ? '' : activeIndex === index ? 'opacity-100' : 'opacity-0',
                  opacitySwitch ? '' : activeIndex === index ? 'bolck' : 'hidden'
                )}
              >
                {content}
              </div>
            ))}
          </div>
        )}

        {/* 内容区域：竖向切换 */}
        {type === 'col' && !!children && (
          <div className={clsx(contentBoxClassName)}>
            {children.map((content, index) => (
              <div
                key={index}
                id={`col${index}`}
                ref={(el) => {
                  if (el) {
                    handleObserve(index, el)
                    elementsRef.current?.set(index, el)
                  } else {
                    elementsRef.current?.delete(index)
                  }
                }}
                className={clsx(childrenBoxClassName, 'w-full h-[inherit]')}
              >
                {content}
              </div>
            ))}
          </div>
        )}

        {/* 内容区域：动态渲染 */}
        {type === 'dynamic' && !!children && (
          <div className={clsx('w-full h-fit', contentBoxClassName)}>
            {children.map((content, index) => (
              <div key={index} className={clsx(childrenBoxClassName, 'w-full h-[inherit]')}>
                {content}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
)

export default SlidingTabs
export type { SlidingTabsRef }
SlidingTabs.displayName = 'SlidingTabs'
